import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
    fetchCatalogItems,
    fetchGlobalCatalogItems,
} from 'entities/CatalogItem';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { addCatalogItemActions } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import { addInventoryItemActions } from 'features/AddInventoryItem/model/slices/AddInventoryItemSlice';
import { getSearchLocalCatalogItemValue } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteGlobalCatalog,
    getRoutePropertiesCatalogInventoryCategories,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFetchWIthController } from 'shared/lib/hooks/useFetchWIthController';

type NavigationType = 'global-marketplace' | 'property-catalog';

interface UseMarketplaceNavigationOptions {
    type?: NavigationType;
}

export const useMarketplaceNavigation = (
    options: UseMarketplaceNavigationOptions = {},
) => {
    const { type = 'global-marketplace' } = options;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const catalogId = useSelector(getSelectedPropertyCatalogId);
    const fetchWithController = useFetchWIthController();

    const navigateToMarketplace = useCallback(() => {
        const propertyId = params.propertyId;
        if (!propertyId) return;

        dispatch(searchCatalogItemsActions.setActive(true));

        if (type === 'global-marketplace') {
            dispatch(addCatalogItemActions.setEditItemMode(true));
            dispatch(
                searchCatalogItemsActions.setGlobalCatItemSearchValue(
                    localSearchValue,
                ),
            );
            dispatch(categoryActions.clearCategorySelection());
            navigate(getRouteGlobalCatalog(propertyId));

            if (localSearchValue) {
                fetchWithController((controller: AbortController) =>
                    dispatch(
                        fetchGlobalCatalogItems({
                            searchString: localSearchValue,
                            abortController: controller,
                        }),
                    ),
                );
            }
        } else {
            dispatch(addInventoryItemActions.setEditItemMode(true));
            navigate(getRoutePropertiesCatalogInventoryCategories(propertyId));

            if (localSearchValue && catalogId) {
                fetchWithController((controller: AbortController) =>
                    dispatch(
                        fetchCatalogItems({
                            data: {
                                catalogId,
                                searchStr: localSearchValue,
                                isInventory: false,
                            },
                            abortController: controller,
                        }),
                    ),
                );
            }
        }
    }, [
        dispatch,
        navigate,
        params.propertyId,
        localSearchValue,
        catalogId,
        fetchWithController,
        type,
    ]);

    return { navigateToMarketplace };
};
